import { useState } from "react";
import {
  getPastSelfMessage,
  setPastSelfMessage,
  shouldAskForMessage,
  shouldShowMessage,
} from "../utils/commitment";
import { hapticSuccess } from "../utils/haptics";
import "../styles/home-surface.css";

/**
 * Messages from your past self.
 *
 * The central problem with any self-directed mental health tool: it asks the
 * user to supply motivation using the exact capacity that low mood impairs.
 * This sidesteps it. We capture motivation on a good day, when they have it in
 * surplus, and hand it back on a bad one — so the push comes from a version of
 * them that was doing well, not from an app telling them what's good for them.
 *
 * Asked only on good days (mood >= 4), surfaced only on bad ones (mood <= 2).
 */
export default function PastSelfMessage({ userId, mood }) {
  const [message, setMessage] = useState(() => getPastSelfMessage(userId));
  const [draft, setDraft] = useState("");
  const [writing, setWriting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const save = () => {
    const saved = setPastSelfMessage(userId, draft);
    if (saved) {
      hapticSuccess();
      setMessage(saved);
      setWriting(false);
      setDraft("");
    }
  };

  // ── Hard day: hand back what they wrote. Their voice, not the app's. ──
  if (shouldShowMessage(mood, message) && !dismissed) {
    return (
      <section className="card ds-stack ds-rise-in" aria-label="A message from you">
        <p className="hs-key hs-key--flush">From you, on a better day</p>
        <blockquote className="ds-prose hs-quote">{message.text}</blockquote>
        <button type="button" className="ds-link" onClick={() => setDismissed(true)}>
          Thank you
        </button>
      </section>
    );
  }

  // ── Good day: capture it while there's some to spare. ──
  if (shouldAskForMessage(mood, message) && !dismissed) {
    if (!writing) {
      return (
        <section className="card ds-stack" aria-label="Leave a note for a harder day">
          <div>
            <p className="ds-lead">
              While you&apos;re feeling good, leave a note for a harder day.
            </p>
            <p className="ds-support" style={{ marginTop: "var(--space-2)" }}>
              A line or two from today, for a day you don&apos;t feel like moving.
              You&apos;ll be the one who gets to read it.
            </p>
          </div>
          <div className="hs-commit-actions">
            <button className="btn btn-primary" onClick={() => setWriting(true)}>
              Write it
            </button>
            <button className="btn btn-quiet" onClick={() => setDismissed(true)}>
              Maybe later
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className="card ds-stack" aria-label="Write a note to your future self">
        <p className="ds-lead">A note to your future self.</p>
        <label>
          <span className="ds-sr-only">Your note</span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            autoFocus
            maxLength={500}
            placeholder="e.g. You have never once regretted going. The first five minutes are the whole of it."
          />
        </label>
        <div className="hs-commit-actions">
          <button className="btn btn-primary" onClick={save} disabled={!draft.trim()}>
            Save it
          </button>
          <button
            className="btn btn-quiet"
            onClick={() => {
              setWriting(false);
              setDraft("");
            }}
          >
            Cancel
          </button>
        </div>
      </section>
    );
  }

  return null;
}
